import { useEffect, useMemo, useState } from 'react';
import {
  type ExternalAccessSnapshot,
  type ExternalAccessTenant,
  type ExternalApiClientRow,
  type ExternalApiEndpointRow,
  type ExternalApiTokenRow,
  useExternalAccessSnapshot,
  useExternalAccessMutations,
  useExternalAccessOverview,
  useTenants,
} from '../hooks/api';
import { formatTimestamp } from '../api';
import { ErrorBox, Loading } from '../components/Loading';
import { Status } from '../components/Status';

export default function ExternalAccessPage() {
  const tenants = useTenants();
  const [tenantCode, setTenantCode] = useState('');

  useEffect(() => {
    if (!tenantCode && tenants.data?.data.length) {
      setTenantCode(tenants.data.data[0]?.code ?? '');
    }
  }, [tenantCode, tenants.data]);

  const overview = useExternalAccessOverview(tenantCode ? { tenantCode, limit: 120 } : { limit: 120 });
  const accessList = overview.data?.data ?? [];
  const access = accessList[0];

  const totals = useMemo(() => {
    const list = overview.data?.data ?? [];
    return {
      clients: list.reduce((sum, tenant) => sum + tenant.clients.length, 0),
      activeTokens: list.reduce(
        (sum, tenant) => sum + tenant.tokens.filter((token) => token.active && !token.revoked_at).length,
        0,
      ),
      logs: list.reduce((sum, tenant) => sum + tenant.recent_logs.length, 0),
    };
  }, [overview.data]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-neutral-300 pb-1">
        <div>
          <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700">
            Externe API-Zugaenge
          </h1>
          <div className="text-[11px] text-neutral-600 font-mono">
            external_api.api_clients / api_tokens / api_endpoints / access_logs
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="input min-w-[260px]"
            value={tenantCode}
            onChange={(event) => setTenantCode(event.target.value)}
          >
            <option value="">Alle Tenants</option>
            {(tenants.data?.data ?? []).map((tenant) => (
              <option key={tenant.id} value={tenant.code}>
                {tenant.code} - {tenant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Kpi label="Clients" value={String(totals.clients)} />
        <Kpi label="Aktive Tokens" value={String(totals.activeTokens)} />
        <Kpi label="Recent Logs" value={String(totals.logs)} />
      </div>

      {overview.isLoading && <Loading />}
      {overview.error && <ErrorBox error={overview.error} />}

      {!overview.isLoading && accessList.length === 0 && (
        <div className="panel">
          <div className="panel-b text-[12px] text-neutral-600">Keine Tenant-Daten gefunden.</div>
        </div>
      )}

      {tenantCode
        ? access && <TenantExternalAccess tenant={access} />
        : accessList.map((tenant) => <TenantExternalAccess key={tenant.tenant_code} tenant={tenant} />)}
    </div>
  );
}

function TenantExternalAccess({ tenant }: { tenant: ExternalAccessTenant }) {
  const client = tenant.clients[0];
  const endpointsByClient = groupByClient(tenant.endpoints);
  const tokensByClient = groupByClient(tenant.tokens);
  const [snapshotPath, setSnapshotPath] = useState<string | null>(null);

  useEffect(() => {
    const firstPath = tenant.endpoints[0]?.path ?? null;
    if (!firstPath) {
      if (snapshotPath !== null) setSnapshotPath(null);
      return;
    }
    if (!snapshotPath || !tenant.endpoints.some((endpoint) => endpoint.path === snapshotPath)) {
      setSnapshotPath(firstPath);
    }
  }, [snapshotPath, tenant.endpoints, tenant.tenant_code]);

  if (tenant.error) {
    return (
      <div className="panel">
        <div className="panel-h">Tenant {tenant.tenant_code}</div>
        <div className="panel-b text-[12px] text-amber-700">{tenant.error}</div>
      </div>
    );
  }

  return (
    <div className="grid gap-3 xl:grid-cols-[460px_1fr]">
      <div className="space-y-3">
        <ClientConfigPanel tenantCode={tenant.tenant_code} client={client} />
        {tenant.clients.map((row) => (
          <TokenPanel
            key={row.id}
            tenantCode={tenant.tenant_code}
            client={row}
            tokens={tokensByClient.get(row.id) ?? []}
          />
        ))}
      </div>

      <div className="space-y-3">
        {tenant.clients.map((row) => (
          <EndpointsPanel
            key={row.id}
            client={row}
            endpoints={endpointsByClient.get(row.id) ?? []}
            selectedSnapshotPath={snapshotPath}
            onSelectSnapshot={setSnapshotPath}
          />
        ))}
        <EndpointSnapshotPanel tenantCode={tenant.tenant_code} endpointPath={snapshotPath} />
        <AccessLogsPanel logs={tenant.recent_logs} />
        <FieldCatalogPanel fields={tenant.field_catalog} />
      </div>
    </div>
  );
}

function ClientConfigPanel({
  tenantCode,
  client,
}: {
  tenantCode: string;
  client: ExternalApiClientRow | undefined;
}) {
  const mutations = useExternalAccessMutations();
  const [processorName, setProcessorName] = useState(client?.processor_name ?? '');
  const [contactRef, setContactRef] = useState(client?.contact_ref ?? '');
  const [avvReference, setAvvReference] = useState(client?.avv_reference ?? '');
  const [ipAllowlist, setIpAllowlist] = useState((client?.ip_allowlist ?? []).join('\n'));
  const [active, setActive] = useState(client?.active ?? false);
  const [reidentificationProhibited, setReidentificationProhibited] = useState(
    client?.reidentification_prohibited ?? true,
  );

  useEffect(() => {
    setProcessorName(client?.processor_name ?? '');
    setContactRef(client?.contact_ref ?? '');
    setAvvReference(client?.avv_reference ?? '');
    setIpAllowlist((client?.ip_allowlist ?? []).join('\n'));
    setActive(client?.active ?? false);
    setReidentificationProhibited(client?.reidentification_prohibited ?? true);
  }, [client?.id, client?.updated_at]);

  const payload = {
    active,
    processorName: processorName.trim() || null,
    contactRef: contactRef.trim() || null,
    avvReference: avvReference.trim() || null,
    reidentificationProhibited,
    ipAllowlist: parseIpAllowlist(ipAllowlist),
  };
  const isSaving = mutations.upsertDefaultClient.isPending || mutations.updateClient.isPending;
  const ipMissing = payload.ipAllowlist.length === 0;
  const avvMissing = !payload.avvReference;

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>Client-Konfiguration</span>
        {client ? (
          active && !client.revoked_at ? <Status kind="ok">active</Status> : <Status kind="warn">prepared</Status>
        ) : (
          <Status kind="warn">fehlt</Status>
        )}
      </div>
      <div className="panel-b space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Dienstleister">
            <input className="input w-full" value={processorName} onChange={(e) => setProcessorName(e.target.value)} />
          </Field>
          <Field label="Kontakt/Referenz">
            <input className="input w-full" value={contactRef} onChange={(e) => setContactRef(e.target.value)} />
          </Field>
        </div>
        <Field label="AVV / Zweckbindung">
          <input className="input w-full" value={avvReference} onChange={(e) => setAvvReference(e.target.value)} />
        </Field>
        <Field label="IP-Allowlist CIDR">
          <textarea
            className="input h-20 w-full font-mono"
            value={ipAllowlist}
            onChange={(e) => setIpAllowlist(e.target.value)}
            placeholder="203.0.113.10/32"
          />
        </Field>
        <div className="flex flex-wrap items-center gap-4 text-[12px]">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            aktiv
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={reidentificationProhibited}
              onChange={(e) => setReidentificationProhibited(e.target.checked)}
            />
            Re-Identifikation verboten
          </label>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {ipMissing && <Status kind="warn">ip fehlt</Status>}
            {avvMissing && <Status kind="warn">avv fehlt</Status>}
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={isSaving}
            onClick={() => {
              if (client) {
                mutations.updateClient.mutate({
                  tenantCode,
                  clientId: client.id,
                  patch: payload,
                });
              } else {
                mutations.upsertDefaultClient.mutate({
                  tenantCode,
                  payload,
                });
              }
            }}
          >
            {client ? 'Speichern' : 'Standardzugang anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TokenPanel({
  tenantCode,
  client,
  tokens,
}: {
  tenantCode: string;
  client: ExternalApiClientRow;
  tokens: ExternalApiTokenRow[];
}) {
  const mutations = useExternalAccessMutations();
  const [label, setLabel] = useState('api-token');
  const [rotationDue, setRotationDue] = useState(defaultDate(90));
  const [expiresAt, setExpiresAt] = useState(defaultDate(180));
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  useEffect(() => {
    if (mutations.createToken.data?.token_value) {
      setGeneratedToken(mutations.createToken.data.token_value);
    }
  }, [mutations.createToken.data]);

  return (
    <div className="panel">
      <div className="panel-h">Tokens: {client.display_name}</div>
      <div className="panel-b space-y-2">
        <div className="grid grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
          <Field label="Label">
            <input className="input w-full" value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Field label="Rotation">
            <input className="input w-full" type="date" value={rotationDue} onChange={(e) => setRotationDue(e.target.value)} />
          </Field>
          <Field label="Ablauf">
            <input className="input w-full" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
          <button
            type="button"
            className="btn-primary"
            disabled={!client.active || mutations.createToken.isPending}
            onClick={() =>
              mutations.createToken.mutate({
                tenantCode,
                clientId: client.id,
                payload: {
                  label: label.trim() || 'api-token',
                  rotationDue: rotationDue || null,
                  expiresAt: expiresAt || null,
                },
              })
            }
          >
            Token erzeugen
          </button>
        </div>

        {generatedToken && (
          <textarea className="input h-16 w-full font-mono" readOnly value={generatedToken} />
        )}

        <table className="tbl">
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Status</th>
              <th>Rotation</th>
              <th>Last used</th>
              <th>IP</th>
              <th className="num">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((token) => (
              <tr key={token.id}>
                <td className="mono">{token.token_prefix}</td>
                <td>{token.active && !token.revoked_at ? <Status kind="ok">active</Status> : <Status kind="err">revoked</Status>}</td>
                <td className="mono">{dateOrDash(token.rotation_due)}</td>
                <td className="mono">{formatTimestamp(token.last_used_at)}</td>
                <td className="mono">{token.last_used_ip ?? '-'}</td>
                <td className="num">
                  <button
                    type="button"
                    className="btn"
                    disabled={!token.active || Boolean(token.revoked_at) || mutations.revokeToken.isPending}
                    onClick={() => {
                      if (window.confirm(`Token ${token.token_prefix} sperren?`)) {
                        mutations.revokeToken.mutate({ tenantCode, tokenId: token.id });
                      }
                    }}
                  >
                    Sperren
                  </button>
                </td>
              </tr>
            ))}
            {tokens.length === 0 && (
              <tr>
                <td colSpan={6} className="text-neutral-600">
                  Keine Tokens.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EndpointsPanel({
  client,
  endpoints,
  selectedSnapshotPath,
  onSelectSnapshot,
}: {
  client: ExternalApiClientRow;
  endpoints: ExternalApiEndpointRow[];
  selectedSnapshotPath: string | null;
  onSelectSnapshot: (path: string) => void;
}) {
  return (
    <div className="panel">
      <div className="panel-h">Zugangspunkte: {client.display_name}</div>
      <div className="panel-b p-0">
        <table className="tbl">
          <thead>
            <tr>
              <th>Methode</th>
              <th>Pfad</th>
              <th>Contract</th>
              <th className="num">Tage</th>
              <th className="num">Rows</th>
              <th className="num">Rate/min</th>
              <th>Status</th>
              <th className="num">Detail</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((endpoint) => (
              <tr key={endpoint.id}>
                <td className="mono">{endpoint.http_method}</td>
                <td>
                  <div className="mono">{endpoint.path}</div>
                  <div className="text-[11px] text-neutral-600">{endpoint.description}</div>
                </td>
                <td className="mono">{endpoint.data_contract_version}</td>
                <td className="num">{endpoint.max_days_per_request}</td>
                <td className="num">{endpoint.max_rows_per_request}</td>
                <td className="num">{endpoint.rate_limit_per_minute}</td>
                <td>{endpoint.active ? <Status kind="ok">active</Status> : <Status kind="muted">inactive</Status>}</td>
                <td className="num">
                  <button
                    type="button"
                    className={endpoint.path === selectedSnapshotPath ? 'btn-primary' : 'btn'}
                    onClick={() => onSelectSnapshot(endpoint.path)}
                  >
                    Detail
                  </button>
                </td>
              </tr>
            ))}
            {endpoints.length === 0 && (
              <tr>
                <td colSpan={8} className="text-neutral-600">
                  Keine Endpoints.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EndpointSnapshotPanel({
  tenantCode,
  endpointPath,
}: {
  tenantCode: string;
  endpointPath: string | null;
}) {
  const snapshot = useExternalAccessSnapshot(tenantCode, endpointPath, 50);
  const data = snapshot.data;

  if (!endpointPath) {
    return (
      <div className="panel">
        <div className="panel-h">Daten-Snapshot</div>
        <div className="panel-b text-[12px] text-neutral-600">Kein Endpoint ausgewaehlt.</div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>Daten-Snapshot: <span className="mono">{endpointPath}</span></span>
        <button
          type="button"
          className="btn"
          disabled={!data?.rows.length}
          onClick={() => data && exportSnapshotRows(data)}
        >
          CSV Download
        </button>
      </div>
      <div className="panel-b space-y-2">
        {snapshot.isLoading && <Loading />}
        {snapshot.error && <ErrorBox error={snapshot.error} />}
        {data && (
          <>
            <div className="grid gap-2 text-[11px] text-neutral-600 md:grid-cols-3">
              <div>
                Quelle: <span className="mono">{data.source.reference}</span>
              </div>
              <div>
                Reihenfolge: <span className="mono">{data.order_by}</span>
              </div>
              <div>
                Stand: <span className="mono">{formatTimestamp(data.generated_at)}</span>
              </div>
            </div>
            <SnapshotTable snapshot={data} />
            <div className="text-[11px] text-neutral-600">
              {data.row_count} Zeilen geladen, Limit {data.limit}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SnapshotTable({ snapshot }: { snapshot: ExternalAccessSnapshot }) {
  if (snapshot.row_count === 0) {
    return <div className="text-[12px] text-neutral-600">Keine Daten fuer diesen Snapshot.</div>;
  }

  return (
    <div className="max-h-[520px] overflow-auto border border-neutral-300">
      <table className="tbl">
        <thead>
          <tr>
            {snapshot.columns.map((column) => (
              <th key={column.name} className="mono">
                {column.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {snapshot.rows.map((row, index) => (
            <tr key={index}>
              {snapshot.columns.map((column) => (
                <td
                  key={column.name}
                  className="mono text-[11px] max-w-[320px] truncate"
                  title={formatCell(row[column.name])}
                >
                  {formatCell(row[column.name])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccessLogsPanel({ logs }: { logs: ExternalAccessTenant['recent_logs'] }) {
  return (
    <div className="panel">
      <div className="panel-h">Access Logs</div>
      <div className="panel-b p-0">
        <table className="tbl">
          <thead>
            <tr>
              <th>Zeit</th>
              <th>Client</th>
              <th>Pfad</th>
              <th>Status</th>
              <th>IP</th>
              <th>Zeitraum</th>
              <th className="num">Rows</th>
              <th className="num">ms</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="mono">{formatTimestamp(log.occurred_at)}</td>
                <td className="mono">{log.client_code ?? log.token_prefix ?? '-'}</td>
                <td className="mono">{log.path}</td>
                <td>
                  {log.allowed ? (
                    <Status kind="ok">{String(log.status_code)}</Status>
                  ) : (
                    <Status kind="err">{log.decision}</Status>
                  )}
                </td>
                <td className="mono">{log.remote_ip ?? '-'}</td>
                <td className="mono">
                  {log.from_date ?? '-'} / {log.to_date ?? '-'}
                </td>
                <td className="num">{log.row_count ?? '-'}</td>
                <td className="num">{log.duration_ms ?? '-'}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr>
                <td colSpan={8} className="text-neutral-600">
                  Keine Zugriffe.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldCatalogPanel({ fields }: { fields: ExternalAccessTenant['field_catalog'] }) {
  return (
    <div className="panel">
      <div className="panel-h">Feldzwecke und Datenminimierung</div>
      <div className="panel-b p-0 max-h-[420px] overflow-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Feld</th>
              <th>Klassifikation</th>
              <th>Quelle</th>
              <th>Zweck</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={`${field.endpoint_path}:${field.field_name}`}>
                <td className="mono">{field.endpoint_path}</td>
                <td className="mono">{field.field_name}</td>
                <td>{field.classification}</td>
                <td className="mono">{field.source_reference}</td>
                <td>{field.purpose}</td>
              </tr>
            ))}
            {fields.length === 0 && (
              <tr>
                <td colSpan={5} className="text-neutral-600">
                  Kein Feldkatalog.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-neutral-600 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="panel">
      <div className="panel-h">{label}</div>
      <div className="panel-b mono text-[18px] font-semibold">{value}</div>
    </div>
  );
}

function groupByClient<T extends { client_id: string }>(rows: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const list = map.get(row.client_id) ?? [];
    list.push(row);
    map.set(row.client_id, list);
  }
  return map;
}

function parseIpAllowlist(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function defaultDate(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().slice(0, 10);
}

function dateOrDash(value: string | null): string {
  return value ? value.slice(0, 10) : '-';
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function exportSnapshotRows(snapshot: ExternalAccessSnapshot): void {
  const headers = snapshot.columns.map((column) => column.name);
  const lines = [
    headers.map(csvCell).join(';'),
    ...snapshot.rows.map((row) => headers.map((header) => csvCell(row[header])).join(';')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeFilePart(snapshot.endpoint.path)}-snapshot.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function safeFilePart(value: string): string {
  return value.replace(/^\/+/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/-+$/, '') || 'external-snapshot';
}
